import * as XLSX from 'xlsx'

const ID_HEADERS = [
  '账户',
  'accountId',
  'account',
  '项目',
  'projectId',
  'project',
  '计划',
  'promotionId',
  'promotion',
  '创意',
  'materialId',
  'material'
]

export function readImportRowsWithRawIds(workbook, sheet, binaryData, fileName) {
  const displayRows = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: '' })

  const htmlRows = readHtmlExcelRows(binaryData)
  if (htmlRows.length) {
    return mergeRawIdColumns(displayRows, htmlRows)
  }

  if (!/\.xlsx$/i.test(fileName || '') || !XLSX.CFB || !XLSX.CFB.read) {
    return displayRows
  }

  try {
    const rawRows = readXlsxRawRows(binaryData, workbook.SheetNames[0])
    return mergeRawIdColumns(displayRows, rawRows)
  } catch (error) {
    console.warn('读取 xlsx 原始 ID 失败:', error)
    return displayRows
  }
}

function readXlsxRawRows(binaryData, sheetName) {
  const cfb = XLSX.CFB.read(binaryData, { type: 'array' })
  const workbookXml = cfbFileText(cfb, 'xl/workbook.xml')
  const relsXml = cfbFileText(cfb, 'xl/_rels/workbook.xml.rels')
  const sheetPath = resolveSheetPath(workbookXml, relsXml, sheetName)
  const sheetXml = cfbFileText(cfb, sheetPath)
  const sharedStrings = readSharedStrings(cfb)
  const rawMatrix = parseSheetXmlRows(sheetXml, sharedStrings)

  return matrixToObjects(rawMatrix)
}

function mergeRawIdColumns(displayRows, rawRows) {
  if (!displayRows.length) {
    return rawRows
  }

  return displayRows.map((row, index) => {
    const rawRow = rawRows[index] || {}
    const merged = { ...row }

    ID_HEADERS.forEach(header => {
      const rawValue = rawRow[header]
      if (rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== '') {
        merged[header] = String(rawValue).trim()
      }
    })

    return merged
  })
}

function readHtmlExcelRows(binaryData) {
  const text = decodeBytes(binaryData)
  const head = text.slice(0, 2048).trimStart().toLowerCase()
  if (!head.startsWith('<html') && !head.includes('<table')) {
    return []
  }

  try {
    const matrix = []
    const rowRegex = /<tr\b[\s\S]*?<\/tr>/gi
    let rowMatch

    while ((rowMatch = rowRegex.exec(text))) {
      const values = []
      const cellRegex = /<t[dh]\b([^>]*)>([\s\S]*?)<\/t[dh]>/gi
      let cellMatch

      while ((cellMatch = cellRegex.exec(rowMatch[0]))) {
        const value = cleanHtmlCellText(cellMatch[2])
        const colspanMatch = cellMatch[1].match(/\bcolspan\s*=\s*["']?(\d+)/i)
        const colspan = Math.max(parseInt(colspanMatch && colspanMatch[1] || '1', 10) || 1, 1)
        values.push(value)
        for (let i = 1; i < colspan; i += 1) {
          values.push('')
        }
      }

      if (values.some(value => String(value || '').trim() !== '')) {
        matrix.push(values)
      }
    }

    const headerIndex = matrix.findIndex(row => {
      const headers = row.map(value => String(value || '').trim())
      return headers.includes('游戏名称') && headers.includes('账户') &&
        (headers.includes('项目') || headers.includes('创意'))
    })

    return headerIndex >= 0 ? matrixToObjects(matrix.slice(headerIndex)) : []
  } catch (error) {
    console.warn('读取 HTML Excel 原始 ID 失败:', error)
    return []
  }
}

function cleanHtmlCellText(html) {
  return decodeHtmlEntities(String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' '))
    .trim()
}

function decodeHtmlEntities(text) {
  const named = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' '
  }

  return String(text || '').replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity) => {
    const key = entity.toLowerCase()
    if (key[0] === '#') {
      const code = key[1] === 'x'
        ? parseInt(key.slice(2), 16)
        : parseInt(key.slice(1), 10)
      return Number.isFinite(code) ? String.fromCharCode(code) : match
    }
    return Object.prototype.hasOwnProperty.call(named, key) ? named[key] : match
  })
}

function resolveSheetPath(workbookXml, relsXml, sheetName) {
  const workbookDoc = parseXml(workbookXml)
  const sheets = Array.from(workbookDoc.getElementsByTagName('sheet'))
  const firstSheet = sheets.find(sheet => !sheetName || sheet.getAttribute('name') === sheetName) || sheets[0]
  const relId = firstSheet && (firstSheet.getAttribute('r:id') || firstSheet.getAttribute('id'))

  if (!relId) {
    return 'xl/worksheets/sheet1.xml'
  }

  const relsDoc = parseXml(relsXml)
  const rels = Array.from(relsDoc.getElementsByTagName('Relationship'))
  const rel = rels.find(item => item.getAttribute('Id') === relId)
  const target = rel && rel.getAttribute('Target')

  return normalizeXlsxPath('xl', target || 'worksheets/sheet1.xml')
}

function readSharedStrings(cfb) {
  const xml = cfbFileText(cfb, 'xl/sharedStrings.xml', true)
  if (!xml) {
    return []
  }

  const doc = parseXml(xml)
  return Array.from(doc.getElementsByTagName('si')).map(item => {
    const textNodes = Array.from(item.getElementsByTagName('t'))
    return textNodes.map(node => node.textContent || '').join('')
  })
}

function parseSheetXmlRows(sheetXml, sharedStrings) {
  if (!sheetXml) {
    return []
  }

  const doc = parseXml(sheetXml)
  const rows = []

  Array.from(doc.getElementsByTagName('row')).forEach(rowNode => {
    const rowNumber = parseInt(rowNode.getAttribute('r'), 10)
    const rowIndex = Number.isFinite(rowNumber) ? rowNumber - 1 : rows.length
    const row = rows[rowIndex] || []

    Array.from(rowNode.getElementsByTagName('c')).forEach(cellNode => {
      const ref = cellNode.getAttribute('r')
      const colIndex = ref ? XLSX.utils.decode_cell(ref).c : row.length
      row[colIndex] = readCellRawText(cellNode, sharedStrings)
    })

    rows[rowIndex] = row
  })

  return rows.filter(row => Array.isArray(row) && row.some(value => String(value || '').trim() !== ''))
}

function readCellRawText(cellNode, sharedStrings) {
  const type = cellNode.getAttribute('t')

  if (type === 'inlineStr') {
    return Array.from(cellNode.getElementsByTagName('t'))
      .map(node => node.textContent || '')
      .join('')
      .trim()
  }

  const valueNode = cellNode.getElementsByTagName('v')[0]
  if (!valueNode) {
    return ''
  }

  const rawValue = (valueNode.textContent || '').trim()
  if (type === 's') {
    return sharedStrings[parseInt(rawValue, 10)] || ''
  }

  return rawValue
}

function matrixToObjects(matrix) {
  if (!matrix.length) {
    return []
  }

  const headers = matrix[0].map(value => String(value || '').trim())

  return matrix.slice(1).map(row => {
    const item = {}
    let hasValue = false

    headers.forEach((header, index) => {
      if (!header) {
        return
      }

      const value = row[index] === undefined || row[index] === null ? '' : String(row[index]).trim()
      item[header] = value
      if (value !== '') {
        hasValue = true
      }
    })

    return hasValue ? item : null
  }).filter(Boolean)
}

function cfbFileText(cfb, path, optional = false) {
  const file = XLSX.CFB.find(cfb, `Root Entry/${path}`) || XLSX.CFB.find(cfb, path)

  if (!file || !file.content) {
    if (optional) {
      return ''
    }
    throw new Error(`xlsx 内缺少 ${path}`)
  }

  if (typeof file.content === 'string') {
    return file.content
  }

  return decodeBytes(file.content)
}

function decodeBytes(content, maxLength) {
  const source = content instanceof Uint8Array
    ? content
    : new Uint8Array(content)
  const bytes = maxLength ? source.slice(0, maxLength) : source

  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(bytes)
  }

  return Array.prototype.map.call(bytes, byte => String.fromCharCode(byte)).join('')
}

function parseXml(xml) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xml || '', 'application/xml')
  const errorNode = doc.getElementsByTagName('parsererror')[0]

  if (errorNode) {
    throw new Error('xlsx XML 解析失败')
  }

  return doc
}

function normalizeXlsxPath(base, target) {
  if (!target) {
    return ''
  }
  if (target.startsWith('/')) {
    return target.replace(/^\/+/, '')
  }

  const stack = base.split('/').filter(Boolean)
  target.split('/').forEach(part => {
    if (!part || part === '.') {
      return
    }
    if (part === '..') {
      stack.pop()
      return
    }
    stack.push(part)
  })

  return stack.join('/')
}
